import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon issue in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const greenIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const redIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

/**
 * Generate a Google Maps directions URL for multiple stops
 */
export const generateGoogleMapsLink = (stops) => {
    if (!stops || stops.length === 0) return '';
    const addresses = stops
        .filter(s => s.address)
        .map(s => encodeURIComponent(s.address));
    if (addresses.length === 0) return '';
    if (addresses.length === 1) {
        return `https://www.google.com/maps/search/?api=1&query=${addresses[0]}`;
    }
    const origin = addresses[0];
    const destination = addresses[addresses.length - 1];
    const waypoints = addresses.slice(1, -1).join('|');
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
    if (waypoints) url += `&waypoints=${waypoints}`;
    return url;
};

/**
 * Generate a Google Maps link for a single address
 */
export const generateSingleMapLink = (address) => {
    if (!address) return '';
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

const MapView = ({ markers = [], height = '400px', className = '', showRoute = false }) => {
    if (!markers || markers.length === 0) {
        return (
            <div className={`bg-muted/50 rounded-xl flex items-center justify-center ${className}`} style={{ height }}>
                <p className="text-muted-foreground text-sm">Aucun point a afficher sur la carte</p>
            </div>
        );
    }

    const validMarkers = markers.filter(m => m.lat && m.lng);
    if (validMarkers.length === 0) {
        return (
            <div className={`bg-muted/50 rounded-xl flex items-center justify-center ${className}`} style={{ height }}>
                <p className="text-muted-foreground text-sm">Aucune adresse geocodee</p>
            </div>
        );
    }

    const center = [
        validMarkers.reduce((s, m) => s + m.lat, 0) / validMarkers.length,
        validMarkers.reduce((s, m) => s + m.lng, 0) / validMarkers.length,
    ];

    return (
        <div className={`rounded-xl overflow-hidden border ${className}`} style={{ height }} data-testid="map-view">
            <MapContainer
                center={center}
                zoom={validMarkers.length === 1 ? 14 : 11}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {validMarkers.map((m, i) => (
                    <Marker
                        key={m.id || i}
                        position={[m.lat, m.lng]}
                        icon={m.type === 'delivery' ? redIcon : greenIcon}
                    >
                        <Popup>
                            <div style={{ minWidth: 150 }}>
                                <strong>{m.label || m.company_name || 'Point'}</strong>
                                {m.address && <p style={{ fontSize: 12, margin: '4px 0' }}>{m.address}</p>}
                                {m.phone && <p style={{ fontSize: 12 }}>Tel: {m.phone}</p>}
                                {m.extra && <p style={{ fontSize: 11, color: '#666' }}>{m.extra}</p>}
                                <a
                                    href={generateSingleMapLink(m.address)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: 12, color: '#1A4D2E', fontWeight: 600 }}
                                >
                                    Ouvrir dans Google Maps
                                </a>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};

export default MapView;
